/**
 * A FIGURE DRAWN IN ITS OWN FRAME (perf overhaul 2b, step 8: "figures cheap, same look").
 *
 * A camera frame used to rewrite every coordinate of every marker - its ring's centre, its stalk's ends, its gauge,
 * its label - although under an orbit almost none of them change shape, they only move. So a marker is drawn
 * relative to its foot, inside one `translate` to where the foot stands this frame: a move rewrites that one
 * attribute, and the browser restyles and lays out next to nothing. Only what truly turns with the camera (a head's
 * faces, a hull's arrow) is still rewritten.
 *
 * Every screen point of a marker comes through its frame (`at` for a world point, `local` for one already on the
 * screen), rounded to a thousandth of a pixel: two projections' difference carries float noise that would otherwise
 * read as a new value every frame. Depth is untouched.
 */
import { project, SCENE_VIEW, type Camera, type ScreenPoint } from '../../engine/battlefield/projection';
import type { Vec3 } from '../../engine/battlefield/types';
import type { DepthPoint, ProjectedScreen, ScreenRun } from '../../engine/display/coverScreens';
import type { ScreenXY } from './markerGeometry';

export interface MarkerFrame {
  /** Where the foot stands on the screen: the marker's `translate`. */
  origin: ScreenXY;
  /** A world point, projected into this frame. */
  at(p: Vec3): ScreenPoint;
  /** A screen point, moved into this frame; whatever else it carries is kept. */
  local<T extends ScreenXY>(q: T): T;
}

/** A thousandth of a pixel: far under what the eye or the hit test can tell, far over float noise. */
const GRAIN = 1000;
const round = (v: number) => Math.round(v * GRAIN) / GRAIN;

/** The frame of a marker whose foot stands at `origin` on the screen, under `camera`. */
export function markerFrame(origin: ScreenXY, camera: Camera): MarkerFrame {
  const local = <T extends ScreenXY>(q: T): T => ({ ...q, x: round(q.x - origin.x), y: round(q.y - origin.y) });
  return { origin, local, at: (p) => local(project(p, camera, SCENE_VIEW)) };
}

const pointsIn = (frame: MarkerFrame, list: readonly DepthPoint[]) => list.map((p) => frame.local(p));

const runIn = (frame: MarkerFrame, run: ScreenRun): ScreenRun => ({
  ...run,
  foot: pointsIn(frame, run.foot),
  top: pointsIn(frame, run.top),
});

/** The take-cover cue's screens round a figure (useCoverCue: projected on the map), moved into its frame. */
export function screensIn(frame: MarkerFrame, screens: readonly ProjectedScreen[]): ProjectedScreen[] {
  return screens.map((s) => ({ ...s, arc: pointsIn(frame, s.arc), runs: s.runs.map((r) => runIn(frame, r)) }));
}
